(() => {
  if (window.__ctPersonalizationModuleReady) return;
  window.__ctPersonalizationModuleReady = true;
  const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
  const ALLOWED_FIELD_TYPES = new Set(['text', 'textarea', 'select', 'file']);
  let instanceCounter = 0;

  const normalize = (value) => String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’‘`]/g, "'")
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();

  const cleanString = (value, fallback = '') => {
    const text = String(value ?? '').replace(/\s+/g, ' ').trim();
    return text || fallback;
  };

  function parseSchemaPayload(rawText) {
    let parsed = JSON.parse(rawText || '{}');
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    if (!Array.isArray(parsed.fields)) return null;
    return parsed;
  }

  function readSchema(container) {
    const node = container.querySelector('[data-ct-personalization-schema]');
    if (!node) return null;

    try {
      return parseSchemaPayload(node.textContent || '{}');
    } catch (error) {
      console.error('[Chubbytrend] Invalid personalization schema.', error);
      return null;
    }
  }

  function selectedOptionValue(form, optionName, container) {
    const expectedName = `options[${optionName}]`;
    const section = container.closest('product-info, [id^="MainProduct-"], .product') || document;
    const candidates = [
      ...form.elements,
      ...section.querySelectorAll('input[name], select[name]')
    ];
    const controls = [...new Set(candidates)].filter((control) =>
      control.name === expectedName || control.name === optionName || control.dataset.optionName === optionName
    );

    const checked = controls.find((control) =>
      (control.type === 'radio' || control.type === 'checkbox') && control.checked
    );
    if (checked) return checked.value;

    const select = controls.find((control) => control.tagName === 'SELECT');
    if (select) return select.value;

    const input = controls.find((control) => control.value);
    return input?.value || '';
  }

  function conditionMatches(form, condition, container) {
    const optionName = condition?.optionName || condition?.option;
    const values = condition?.values || condition?.valueMatches || [];
    if (!optionName || !Array.isArray(values)) return false;

    const current = normalize(selectedOptionValue(form, optionName, container));
    if (!current) return false;
    return values.some((value) => normalize(value) === current);
  }

  function updateConditionalFields(container, form, schema) {
    if (!schema?.fields) return;

    schema.fields.forEach((schemaField) => {
      const key = String(schemaField.key || '');
      const field = [...container.querySelectorAll('[data-ct-schema-field-key]')]
        .find((node) => node.dataset.ctSchemaFieldKey === key);
      if (!field) return;

      const hideWhen = schemaField.conditions?.hideWhen || [];
      const showWhen = schemaField.conditions?.showWhen || [];
      const hiddenByHideRule = hideWhen.some((condition) => conditionMatches(form, condition, container));
      const hiddenByShowRule = showWhen.length > 0 && !showWhen.some((condition) => conditionMatches(form, condition, container));
      const hidden = hiddenByHideRule || hiddenByShowRule;

      field.hidden = hidden;
      field.setAttribute('aria-hidden', hidden ? 'true' : 'false');

      field.querySelectorAll('[data-ct-personalization-input]').forEach((input) => {
        input.disabled = hidden;
        const originallyRequired = input.dataset.ctOriginalRequired === 'true';
        input.required = !hidden && originallyRequired;
        if (!hidden && originallyRequired) input.setAttribute('aria-required', 'true');
        else input.removeAttribute('aria-required');
      });
    });

    const visibleFields = [...container.querySelectorAll('[data-ct-personalization-field]')]
      .filter((field) => !field.hidden && !field.hasAttribute('data-ct-schema-fallback'));
    container.classList.toggle('ct-personalization--empty', visibleFields.length === 0);
  }

  function appendText(parent, tagName, className, text) {
    const node = document.createElement(tagName);
    if (className) node.className = className;
    node.textContent = text;
    parent.appendChild(node);
    return node;
  }

  function normalizedFieldType(field) {
    const candidate = normalize(field?.type || field?.inputType || 'text');
    return ALLOWED_FIELD_TYPES.has(candidate) ? candidate : 'text';
  }

  function normalizedOptions(field) {
    if (!Array.isArray(field?.options)) return [];
    return field.options
      .map((option) => {
        if (typeof option === 'string' || typeof option === 'number') {
          const value = cleanString(option);
          return value ? { value, label: value } : null;
        }
        if (option && typeof option === 'object') {
          const value = cleanString(option.value ?? option.label);
          const label = cleanString(option.label ?? option.value, value);
          return value ? { value, label } : null;
        }
        return null;
      })
      .filter(Boolean);
  }

  function createSchemaField(field, container, form, instanceId, fieldIndex) {
    if (!field || typeof field !== 'object') return null;

    const key = cleanString(field.key, `custom_text_${fieldIndex + 1}`);
    const labelText = cleanString(field.label, key.replaceAll('_', ' '));
    const propertyName = cleanString(field.propertyName, labelText);
    const type = normalizedFieldType(field);
    const required = field.required === true;
    const maxLengthValue = Number(field.maxLength);
    const maxLength = Number.isInteger(maxLengthValue) && maxLengthValue > 0
      ? Math.min(maxLengthValue, 5000)
      : null;
    const inputId = `ct-persona-schema-${instanceId}-${fieldIndex}-${normalize(key).replace(/[^a-z0-9]+/g, '-')}`;

    const wrapper = document.createElement('div');
    wrapper.className = 'ct-personalization__field';
    wrapper.dataset.ctPersonalizationField = '';
    wrapper.dataset.ctSchemaFieldKey = key;
    wrapper.dataset.fieldType = type;

    const label = document.createElement('label');
    label.className = 'ct-personalization__label';
    label.htmlFor = inputId;
    label.append(document.createTextNode(labelText));

    const requiredMarker = document.createElement('span');
    requiredMarker.className = 'ct-personalization__required';
    requiredMarker.dataset.ctRequiredMarker = '';
    requiredMarker.setAttribute('aria-hidden', 'true');
    requiredMarker.textContent = '*';
    requiredMarker.hidden = !required;
    label.append(document.createTextNode(' '), requiredMarker);
    wrapper.appendChild(label);

    let input;
    if (type === 'textarea') {
      input = document.createElement('textarea');
      input.className = 'ct-personalization__input ct-personalization__textarea';
    } else if (type === 'select') {
      input = document.createElement('select');
      input.className = 'ct-personalization__input ct-personalization__select';
      const emptyOption = document.createElement('option');
      emptyOption.value = '';
      emptyOption.textContent = `Select ${labelText}`;
      input.appendChild(emptyOption);
      normalizedOptions(field).forEach((option) => {
        const optionNode = document.createElement('option');
        optionNode.value = option.value;
        optionNode.textContent = option.label;
        input.appendChild(optionNode);
      });
    } else {
      input = document.createElement('input');
      input.type = type === 'file' ? 'file' : 'text';
      input.className = type === 'file'
        ? 'ct-personalization__input ct-personalization__file'
        : 'ct-personalization__input';
    }

    input.id = inputId;
    input.name = `properties[${propertyName}]`;
    input.dataset.ctPersonalizationInput = '';
    input.dataset.ctOriginalRequired = required ? 'true' : 'false';
    input.required = required;
    if (required) input.setAttribute('aria-required', 'true');

    const placeholder = cleanString(field.placeholder);
    if (placeholder && type !== 'file' && type !== 'select') input.placeholder = placeholder;
    if (maxLength && type !== 'file' && type !== 'select') input.maxLength = maxLength;

    if (type === 'file') {
      input.accept = cleanString(field.accept, 'image/jpeg,image/png,image/webp');
      input.dataset.ctFileInput = '';
      form.enctype = 'multipart/form-data';
    }

    wrapper.appendChild(input);

    if (type === 'file') {
      const fileName = appendText(wrapper, 'p', 'ct-personalization__file-name', '');
      fileName.dataset.ctFileName = '';
      fileName.hidden = true;
    }

    const helpText = cleanString(field.helpText);
    if (helpText) appendText(wrapper, 'p', 'ct-personalization__help', helpText);

    if (maxLength && type !== 'file' && type !== 'select') {
      const counter = document.createElement('p');
      counter.className = 'ct-personalization__counter';
      counter.setAttribute('aria-live', 'polite');
      const count = document.createElement('span');
      count.dataset.ctCharacterCount = '';
      count.textContent = '0';
      counter.append(count, document.createTextNode(`/${maxLength}`));
      wrapper.appendChild(counter);
    }

    return wrapper;
  }

  function renderSchemaText(container, form, schema) {
    if (container.dataset.ctSchemaTextMode !== 'true') return false;
    if (!schema || !Array.isArray(schema.fields)) return false;

    const mount = container.querySelector('[data-ct-schema-mount]');
    const fallback = container.querySelector('[data-ct-schema-fallback]');
    const fallbackInput = container.querySelector('[data-ct-schema-fallback-input]');
    if (!mount) return false;

    instanceCounter += 1;
    const instanceId = `${Date.now().toString(36)}-${instanceCounter}`;
    const fragment = document.createDocumentFragment();
    let renderedCount = 0;

    schema.fields.forEach((field, index) => {
      const renderedField = createSchemaField(field, container, form, instanceId, index);
      if (!renderedField) return;
      fragment.appendChild(renderedField);
      renderedCount += 1;
    });

    if (renderedCount === 0) return false;

    mount.replaceChildren(fragment);
    mount.hidden = false;
    if (fallback) fallback.hidden = true;
    if (fallbackInput) fallbackInput.disabled = true;

    const strategyInput = container.querySelector('[data-ct-personalization-strategy]');
    const versionInput = container.querySelector('[data-ct-personalization-version]');
    if (strategyInput) strategyInput.value = cleanString(schema.strategy, 'schema');
    if (versionInput) versionInput.value = String(schema.version || 1);
    container.dataset.renderStrategy = cleanString(schema.strategy, 'schema');

    const title = container.querySelector('[data-ct-personalization-title]');
    const instructions = container.querySelector('[data-ct-personalization-instructions]');
    if (title && cleanString(schema.title)) title.textContent = cleanString(schema.title);
    if (
      instructions &&
      instructions.dataset.productInstructions !== 'true' &&
      cleanString(schema.instructions)
    ) {
      instructions.textContent = cleanString(schema.instructions);
      instructions.hidden = false;
    }

    return true;
  }

  function setupCounters(container) {
    container.querySelectorAll('[maxlength]').forEach((input) => {
      const field = input.closest('[data-ct-personalization-field]');
      const counter = field?.querySelector('[data-ct-character-count]');
      if (!counter || input.dataset.ctCounterReady === 'true') return;
      input.dataset.ctCounterReady = 'true';
      const update = () => { counter.textContent = String(input.value.length); };
      input.addEventListener('input', update);
      update();
    });
  }

  function setupFileInputs(container) {
    container.querySelectorAll('[data-ct-file-input]').forEach((input) => {
      if (input.dataset.ctFileReady === 'true') return;
      input.dataset.ctFileReady = 'true';
      const field = input.closest('[data-ct-personalization-field]');
      const fileName = field?.querySelector('[data-ct-file-name]');
      input.addEventListener('change', () => {
        const file = input.files?.[0];
        if (!fileName) return;
        fileName.hidden = !file;
        fileName.textContent = file ? file.name : '';
      });
    });
  }

  function setupInstructionsToggle(container) {
    const instructions = container.querySelector('[data-ct-personalization-instructions]');
    if (!instructions || instructions.hidden || !instructions.textContent.trim()) return;
    if (instructions.dataset.ctReadMoreReady === 'true') return;

    instructions.dataset.ctReadMoreReady = 'true';
    if (!instructions.id) {
      instanceCounter += 1;
      instructions.id = `ct-persona-instructions-${Date.now().toString(36)}-${instanceCounter}`;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'ct-personalization__instructions-wrap';
    instructions.parentNode.insertBefore(wrapper, instructions);
    wrapper.appendChild(instructions);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ct-personalization__read-more';
    button.textContent = '... Read more';
    button.setAttribute('aria-controls', instructions.id);
    button.setAttribute('aria-expanded', 'false');
    button.hidden = true;
    wrapper.appendChild(button);

    const measureOverflow = () => {
      if (wrapper.classList.contains('is-expanded')) return;

      instructions.classList.add('ct-personalization__instructions--clamped');
      window.requestAnimationFrame(() => {
        const hasOverflow = instructions.scrollHeight > instructions.clientHeight + 1;
        button.hidden = !hasOverflow;
        if (!hasOverflow) {
          instructions.classList.remove('ct-personalization__instructions--clamped');
        }
      });
    };

    button.addEventListener('click', () => {
      const expanded = wrapper.classList.toggle('is-expanded');
      instructions.classList.toggle('ct-personalization__instructions--clamped', !expanded);
      button.textContent = expanded ? 'Show less' : '... Read more';
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');

      if (!expanded) measureOverflow();
    });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(measureOverflow);
      resizeObserver.observe(instructions);
    } else {
      window.addEventListener('resize', measureOverflow, { passive: true });
    }

    measureOverflow();
  }

  function initContainer(container) {
    if (container.dataset.ctPersonalizationReady === 'true') return;
    container.dataset.ctPersonalizationReady = 'true';

    const form = container.closest('form[action*="/cart/add"]') || container.closest('form');
    if (!form) return;

    const error = container.querySelector('[data-ct-personalization-error]');
    const schema = readSchema(container);
    const schemaRendered = renderSchemaText(container, form, schema);

    if (container.dataset.ctSchemaTextMode === 'true' && !schemaRendered) {
      const fallback = container.querySelector('[data-ct-schema-fallback]');
      const fallbackInput = container.querySelector('[data-ct-schema-fallback-input]');
      if (fallback) fallback.hidden = false;
      if (fallbackInput) fallbackInput.disabled = false;
      const strategyInput = container.querySelector('[data-ct-personalization-strategy]');
      if (strategyInput) strategyInput.value = 'generic-fallback';
      container.dataset.renderStrategy = 'generic-fallback';
      console.warn('[Chubbytrend] Schema text could not be rendered. Generic personalization fallback remains active.');
    }

    setupInstructionsToggle(container);
    setupCounters(container);
    setupFileInputs(container);
    container.dispatchEvent(new CustomEvent('ct:personalization-ready', { bubbles: true }));

    const schemaUsable = Boolean(
      schema && (schemaRendered || container.dataset.ctSchemaTextMode !== 'true')
    );

    if (schemaUsable) {
      const refresh = () => updateConditionalFields(container, form, schema);
      form.addEventListener('change', refresh);
      document.addEventListener('variant:change', refresh);
      refresh();
    }

    form.addEventListener('submit', (event) => {
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }

      for (const input of container.querySelectorAll('[data-ct-file-input]:not(:disabled)')) {
        const file = input.files?.[0];
        if (file && file.size > MAX_FILE_SIZE_BYTES) {
          event.preventDefault();
          event.stopImmediatePropagation();
          input.focus();
          if (error) {
            error.textContent = 'Each uploaded image must be 10 MB or smaller.';
            error.hidden = false;
          }
          return;
        }
      }

      if (!form.checkValidity()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        form.reportValidity();
      }
    }, true);
  }

  function initAll(root = document) {
    root.querySelectorAll('[data-ct-personalization]').forEach(initContainer);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
